<template>
    <div v-if="passwordChangingAllowed">
        <div class="field">
            <label>{{"components.pages.profile.password.old password" | translate}}</label>
            <div class="control has-icons-left has-icons-right">
                <input
                    v-model="passwordChange.oldPassword"
                    v-validate="`required|min:4|max:50`"
                    data-vv-name="oldPassword"
                    data-vv-validate-on="change"
                    :error-messages="errors.collect('oldPassword')"
                    :class="{'is-danger': errors.has('oldPassword')}"
                    :type="passwordChange.showPassword ? 'text' : 'password'"
                    class="input"
                    ref="oldPassword"
                />
                <span class="icon is-small is-left">
                    <i class="fas fa-lock"></i>
                </span>
                <a
                    class="icon is-small is-right bng-clickable"
                    @click="toggleShowPassword('oldPassword')"
                >
                    <span v-show="!passwordChange.showPassword">
                        <i class="far fa-eye-slash"></i>
                    </span>
                    <span v-show="passwordChange.showPassword">
                        <i class="far fa-eye"></i>
                    </span>
                </a>
            </div>
            <p
                class="help is-danger"
                v-if="errors.has('oldPassword:required')"
            >{{"components.pages.profile.validation.field required" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('oldPassword:min')"
            >{{"components.pages.profile.validation.password min length" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('oldPassword:max')"
            >{{"components.pages.profile.validation.password max length" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('oldPassword:wrongOldPassword')"
            >{{"components.pages.profile.validation.wrong old password" | translate}}</p>
        </div>
        <div class="field">
            <label>{{"components.pages.profile.password.new password" | translate}}</label>
            <div class="control has-icons-left has-icons-right">
                <input
                    v-model="passwordChange.newPassword"
                    v-validate="`required|min:4|max:50`"
                    data-vv-name="newPassword"
                    data-vv-validate-on="change"
                    :error-messages="errors.collect('newPassword')"
                    :class="{'is-danger': errors.has('newPassword')}"
                    :type="passwordChange.showPassword ? 'text' : 'password'"
                    class="input"
                    ref="newPassword"
                />
                <span class="icon is-small is-left">
                    <i class="fas fa-lock"></i>
                </span>
                <a
                    class="icon is-small is-right bng-clickable"
                    @click="toggleShowPassword('newPassword')"
                >
                    <span v-show="!passwordChange.showPassword">
                        <i class="far fa-eye-slash"></i>
                    </span>
                    <span v-show="passwordChange.showPassword">
                        <i class="far fa-eye"></i>
                    </span>
                </a>
            </div>
            <p
                class="help is-danger"
                v-if="errors.has('newPassword:required')"
            >{{"components.pages.profile.validation.field required" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('newPassword:min')"
            >{{"components.pages.profile.validation.password min length" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('newPassword:max')"
            >{{"components.pages.profile.validation.password max length" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('newPassword:invalidNewPassword')"
            >{{"components.pages.profile.validation.invalid new password" | translate}}</p>
        </div>
        <div class="field">
            <label>{{"components.pages.profile.password.repeat new password" | translate}}</label>
            <div class="control has-icons-left has-icons-right">
                <input
                    v-model="passwordChange.confirmedNewPassword"
                    v-validate="`required|confirmed:newPassword`"
                    data-vv-name="confirmedNewPassword"
                    data-vv-validate-on="change"
                    :error-messages="errors.collect('confirmedNewPassword')"
                    :class="{'is-danger': errors.has('confirmedNewPassword')}"
                    :type="passwordChange.showPassword ? 'text' : 'password'"
                    class="input"
                    ref="confirmedNewPassword"
                />
                <span class="icon is-small is-left">
                    <i class="fas fa-lock"></i>
                </span>
                <a
                    class="icon is-small is-right bng-clickable"
                    @click="toggleShowPassword('newPassword')"
                >
                    <span v-show="!passwordChange.showPassword">
                        <i class="far fa-eye-slash"></i>
                    </span>
                    <span v-show="passwordChange.showPassword">
                        <i class="far fa-eye"></i>
                    </span>
                </a>
            </div>
            <p
                class="help is-danger"
                v-if="errors.has('confirmedNewPassword:required')"
            >{{"components.pages.profile.validation.field required" | translate}}</p>
            <p
                class="help is-danger"
                v-if="errors.has('confirmedNewPassword:confirmed')"
            >{{"components.pages.profile.validation.password confirmed" | translate}}</p>
        </div>
        <div class="field">
            <div class="control submit-buttons">
                <button
                    class="button is-link button-submit"
                    @click="changePassword()"
                    :disabled="errors.has('oldPassword') || errors.has('newPassword') || errors.has('confirmedNewPassword')"
                >{{"components.pages.profile.password.save" | translate}}</button>
            </div>
        </div>
        <div class="field" v-if="passwordChange.isSuccess">
            <label class="label has-text-success">{{"components.pages.profile.password.success" | translate}}</label>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';

import authService from '../../../../../../_common/services/authentication.service';

export default {
    name: 'profile-password',
    $_veeValidate: { validator: 'new' },
    data() {
        return {
            passwordChangingAllowed: false,
            passwordChange: {
                showPassword: false,
                oldPassword: null,
                newPassword: null,
                confirmedNewPassword: null,
                isSuccess: false
            }
        };
    },
    async created() {
        await Vue.state.client.ensure();
        let client = Vue.state.client.get();
        this.passwordChangingAllowed = client.view.profile.passwordChangingAllowed;
    },
    methods: {
        async changePassword() {
            if (
                this.errors.has('oldPassword') ||
                this.errors.has('newPassword') ||
                this.errors.has('confirmedNewPassword') ||
                !this.passwordChange.oldPassword ||
                !this.passwordChange.newPassword ||
                !this.passwordChange.confirmedNewPassword
            )
                return;
            this.passwordChange.error = null;
            const result = await authService.changePassword(this.passwordChange.oldPassword, this.passwordChange.newPassword);
            this.passwordChange.isSuccess = result.isSuccess;
            this.passwordChange.error = result.error;
            if (result.isSuccess) {
                this.passwordChange.showPassword = false;
                this.passwordChange.oldPassword = null;
                this.passwordChange.newPassword = null;
                this.passwordChange.confirmedNewPassword = null;
            } else {
                this.errors.add({ field: result.errorField, rule: result.error, msg: result.error });
            }
        },
        toggleShowPassword(ref) {
            this.passwordChange.showPassword = !this.passwordChange.showPassword;
            setTimeout(() => this.$refs[ref].focus());
        }
    }
};
</script>



// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/pages/sitepages/profile/password.vue